// Renders the response report (see supportResponseTimes.js) as copy-pasteable text.
//
// The report BODY is deliberately Norwegian: it is an internal artifact for Norwegian
// support staff, pasted into email/Teams/Excel. UI chrome around it is translated the
// normal way with <Text>.
//
// Pure: takes plain data, returns strings. No Preact, no store, no util imports.

const MONTHS_NO = ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'];

const PARTY_LABEL = {
    staff: 'Oss',
    customer: 'Kunde',
    ai: 'AI',
    system: 'System',
};

export const INPUT_SOURCE_LABEL = {
    phone: 'telefon',
    external_email: 'e-post',
    in_person: 'oppmøte',
    other: 'annet',
};

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

const pad2 = value => String(value).padStart(2, '0');

/**
 * Human-readable duration: minutes below an hour, hours+minutes below a day,
 * days+hours above. Null/undefined render as an em dash.
 */
export function formatDuration(ms) {
    if (ms === null || ms === undefined) return '—';
    if (ms >= DAY_MS) {
        const days = Math.floor(ms / DAY_MS);
        const hours = Math.floor((ms % DAY_MS) / HOUR_MS);
        return `${days}d ${hours}t`;
    }
    const totalMinutes = Math.round(ms / MINUTE_MS);
    if (totalMinutes < 60) return `${totalMinutes}m`;
    return `${Math.floor(totalMinutes / 60)}t ${pad2(totalMinutes % 60)}m`;
}

// "12. aug 14:02"
const stamp = (date) => {
    if (!date) return '';
    return `${date.getDate()}. ${MONTHS_NO[date.getMonth()]} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

// "2026-08-12 16:31"
const isoStamp = (date) => {
    if (!date) return '';
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())} `
        + `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

const userName = (info) => {
    if (!info) return '';
    return `${info.firstname || ''} ${info.lastname || ''}`.trim();
};

// Collapse control characters that would break either the aligned text table or the
// TSV column structure.
const oneLine = value => String(value === null || value === undefined ? '' : value)
    .replace(/[\t\r\n]+/g, ' ')
    .trim();

const tsvSafe = oneLine;

const partySummary = (label, party) => {
    if (!party || party.count === 0) return `${label} ingen svar ennå`;
    const parts = [`${party.count} svar`, `snitt ${formatDuration(party.avgMs)}`];
    if (party.count > 1) {
        parts.push(`raskest ${formatDuration(party.minMs)}`);
        parts.push(`tregest ${formatDuration(party.maxMs)}`);
    }
    return `${label} ${parts.join(' · ')}`;
};

const annotation = (turn) => {
    if (!turn.counted) {
        // AI auto-replies are uncounted AND automated. System rows (e.g. a status change like
        // "Customer reopened this ticket") are uncounted but are real human actions, so they
        // must not be labelled "auto".
        return turn.party === 'ai' ? '(auto, ikke medregnet)' : '(ikke medregnet)';
    }
    const notes = [];
    if (turn.loggedBy) {
        const how = INPUT_SOURCE_LABEL[turn.inputSource];
        const sanitizedBy = oneLine(turn.loggedBy);
        notes.push(how ? `logget av ${sanitizedBy}, ${how}` : `logget av ${sanitizedBy}`);
    }
    if (turn.messageCount > 1) notes.push(`${turn.messageCount} meldinger`);
    return notes.length > 0 ? `(${notes.join(', ')})` : '';
};

/**
 * Aligned plain-text report, for pasting into email, Teams or a document.
 */
export function reportAsText(ticket = {}, report) {
    const rep = report || { turns: [], staff: {}, customer: {}, perPerson: [], totals: {} };
    const turns = rep.turns || [];
    const totals = rep.totals || {};

    const rows = turns.map(turn => ({
        num: turn.counted ? String(turn.index) : '–',
        party: PARTY_LABEL[turn.party] || turn.party,
        name: oneLine(turn.name || ''),
        when: stamp(turn.date),
        duration: turn.counted
            ? formatDuration(turn.responseMs)
            : '',
        note: annotation(turn),
    }));

    const width = (key, heading) => Math.max(heading.length, ...rows.map(r => r[key].length), 0);
    const wNum = width('num', '#');
    const wParty = width('party', 'Part');
    const wName = width('name', 'Navn');
    const wWhen = width('when', 'Tidspunkt');
    const wDuration = width('duration', 'Svartid');

    const line = (num, party, name, when, duration, note) => (
        `  ${num.padEnd(wNum)}  ${party.padEnd(wParty)}  ${name.padEnd(wName)}  `
        + `${when.padEnd(wWhen)}  ${duration.padEnd(wDuration)}${note ? `  ${note}` : ''}`
    ).replace(/\s+$/, '');

    const out = [];
    const number = ticket.ticketNumber ? `#${ticket.ticketNumber}` : '';
    out.push(`SVARRAPPORT — ${[number, ticket.subject].filter(Boolean).join(' ')}`.trim());

    const who = [
        ticket.customerInfo && ticket.customerInfo.name ? `Kunde: ${ticket.customerInfo.name}` : null,
        userName(ticket.userInfo) ? `Bruker: ${userName(ticket.userInfo)}` : null,
    ].filter(Boolean).join(' · ');
    if (who) out.push(who);

    const meta = [
        ticket.createdDate ? `Opprettet: ${stamp(new Date(ticket.createdDate))}` : null,
        ticket.status ? `Status: ${String(ticket.status).replace(/_/g, ' ')}` : null,
    ].filter(Boolean).join(' · ');
    if (meta) out.push(meta);

    out.push('');
    out.push(line('#', 'Part', 'Navn', 'Tidspunkt', 'Svartid', ''));
    rows.forEach(r => out.push(line(r.num, r.party, r.name, r.when, r.duration, r.note)));
    out.push('');

    out.push(partySummary('Oss:', rep.staff));
    out.push(partySummary('Kunde:', rep.customer));

    const totalsLine = [
        `Første menneskelige svar: ${totals.firstHumanResponseMs === null || totals.firstHumanResponseMs === undefined
            ? 'ingen menneskelige svar'
            : formatDuration(totals.firstHumanResponseMs)}`,
        `Totalt åpen tid: ${formatDuration(totals.openMs)}`,
    ].join(' · ');
    out.push(totalsLine);

    if (totals.aiReplyCount > 0) {
        out.push(`AI-svar: ${totals.aiReplyCount} (ikke medregnet)`);
    }
    if (totals.undatedMessageCount > 0) {
        out.push(`Meldinger uten tidsstempel: ${totals.undatedMessageCount} (utelatt fra beregningen)`);
    }

    const perPerson = rep.perPerson || [];
    if (perPerson.length > 0) {
        const sanitizedPeople = perPerson.map(p => ({ ...p, name: oneLine(p.name) }));
        const wPerson = Math.max(...sanitizedPeople.map(p => p.name.length));
        out.push('');
        out.push('Per medarbeider:');
        sanitizedPeople.forEach((person) => {
            out.push(`  ${person.name.padEnd(wPerson)}  ${person.count} svar · snitt ${formatDuration(person.avgMs)}`);
        });
    }

    return out.join('\n');
}

/**
 * Tab-separated rows for pasting into Excel/Sheets. Durations are whole minutes so the
 * column parses as a number in every locale — in nb-NO Excel a decimal POINT is the date
 * separator, so a decimal-hours value like "2.48" is misread as a date/text and breaks
 * AVERAGE(). Integers parse identically everywhere.
 */
export function reportAsTsv(report) {
    const rep = report || { turns: [] };
    const turns = rep.turns || [];
    const lines = [['Part', 'Navn', 'Tidspunkt', 'Svartid (min)', 'Medregnet', 'Logget av'].join('\t')];

    turns.forEach((turn) => {
        const minutes = turn.counted && turn.responseMs !== null && turn.responseMs !== undefined
            ? String(Math.round(turn.responseMs / MINUTE_MS))
            : '';
        lines.push([
            tsvSafe(PARTY_LABEL[turn.party] || turn.party),
            tsvSafe(turn.name),
            tsvSafe(isoStamp(turn.date)),
            tsvSafe(minutes),
            turn.counted ? 'ja' : 'nei',
            tsvSafe(turn.loggedBy),
        ].join('\t'));
    });

    return lines.join('\n');
}
